import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faSpinner, faExclamationTriangle, faFilm, faTv, faArrowLeft, faTimes } from '@fortawesome/free-solid-svg-icons';
import api, { WatchlistAPI } from '../../api';
import ContentCard from '../Cards/ContentCard';
import { UserComments } from '../Comments';

const ProfileContainer = styled.div`
  padding: 100px 50px 30px;
  
  @media (max-width: 768px) {
    padding: 100px 20px 20px;
  }
`;

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const AvatarContainer = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  overflow: hidden;
  background-color: var(--background-light);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 30px;
  border: 3px solid var(--primary-light);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  svg {
    font-size: 60px;
    color: var(--text-dim);
  }
  
  @media (max-width: 768px) {
    margin-right: 0;
    margin-bottom: 20px;
  }
`;

const UserInfo = styled.div`
  flex: 1;
`;

const Username = styled.h1`
  font-size: 32px;
  margin-bottom: 5px;
`;

const FullName = styled.h2`
  font-size: 20px;
  color: var(--text-dim);
  margin-bottom: 10px;
`;

const Bio = styled.p`
  color: var(--text-dim);
  margin-bottom: 15px;
  max-width: 600px;
  line-height: 1.6;
`;

const JoinDate = styled.div`
  color: var(--text-dim);
  font-size: 14px;
  margin-top: 10px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  
  svg {
    font-size: 40px;
    color: var(--primary-light);
    margin-bottom: 20px;
    animation: spin 1s linear infinite;
    
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  }
  
  p {
    color: var(--text-dim);
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 25px;
  margin: 15px auto;
  max-width: 600px;
  animation: fadeIn 0.3s ease-in-out;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  svg {
    font-size: 28px;
    color: #ef4444;
    margin-bottom: 15px;
  }
  
  h3 {
    font-size: 18px;
    margin-bottom: 10px;
    text-align: center;
  }
  
  p {
    color: var(--text-dim);
    text-align: center;
    margin-bottom: 20px;
    font-size: 14px;
  }
  
  button {
    margin-top: 5px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  margin-bottom: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 10px;
`;

const WatchlistGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  
  h3 {
    font-size: 20px;
    margin-bottom: 10px;
  }
  
  p {
    color: var(--text-dim);
  }
`;

const UserStats = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 15px;
  flex-wrap: wrap;
`;

const StatItem = styled.div`
  background-color: rgba(255, 255, 255, 0.05);
  padding: 10px 15px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 8px;
    color: var(--primary-light);
  }
  
  span {
    color: var(--text-dim);
    margin-right: 5px;
  }
  
  strong {
    color: white;
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: var(--border-radius);
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
  
  &:hover {
    background-color: var(--primary-light);
    transform: translateY(-2px);
    box-shadow: 0 3px 8px rgba(59, 130, 246, 0.4);
  }
`;

const formatDate = (dateString) => {
  if (!dateString) return 'Unknown';
  
  try {
    // Parse the UTC date string and ensure proper timezone handling
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Unknown';
    }
    
    // Format using browser's locale settings
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone // Explicitly use user's timezone
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Unknown';
  }
};

const UserProfilePage = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userWatchlist, setUserWatchlist] = useState([]);
  const [loadingWatchlist, setLoadingWatchlist] = useState(false);
  const [watchlistError, setWatchlistError] = useState(null);
  
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        setError(null); // Clear previous errors
        
        // Use the auth endpoint to get user profile by username
        const response = await api.get(`/auth/profile/username/${username}`);
        
        if (response.data.success) {
          setUser(response.data.data);
        } else {
          setError('User not found');
        }
      } catch (err) {
        console.error('Error fetching user profile:', err);
        
        // Check for rate limit error
        if (err.isRateLimit) {
          setError("You've reached the rate limit. Please wait a moment before trying again.");
        } else {
          setError(err.response?.data?.detail?.message || 'Failed to load user profile');
        }
      } finally {
        setLoading(false);
      }
    };
    
    if (username) {
      fetchUserProfile();
    }
  }, [username]);
  
  // Fetch user's watchlist
  useEffect(() => {
    const fetchUserWatchlist = async () => {
      if (!user || !user._id) {
        return; // Exit early if user is null or has no ID
      }
      
      try {
        setLoadingWatchlist(true);
        setWatchlistError(null); // Clear previous errors
        
        const response = await WatchlistAPI.getUserWatchlist(user._id);
        if (response.data.success) {
          setUserWatchlist(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching user watchlist:', error);
        
        // Check for rate limit error
        if (error.isRateLimit) {
          setWatchlistError("You've reached the rate limit. Please wait a moment before trying again.");
        } else {
          setWatchlistError('Failed to load watchlist. Please try again later.');
        }
      } finally {
        setLoadingWatchlist(false);
      }
    };
    
    fetchUserWatchlist();
  }, [user]);
  
  const clearError = () => {
    setError(null);
  };
  
  const clearWatchlistError = () => {
    setWatchlistError(null);
  };
  
  if (loading) {
    return (
      <ProfileContainer>
        <LoadingContainer>
          <FontAwesomeIcon icon={faSpinner} />
          <p>Loading user profile...</p>
        </LoadingContainer>
      </ProfileContainer>
    );
  }
  
  if (error || !user) {
    return (
      <ProfileContainer>
        <ErrorContainer>
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <h3>{error || 'Error Loading Profile'}</h3>
          <p>{error ? 'We couldn\'t load this user profile.' : 'We couldn\'t load this user profile. Please try again later.'}</p>
          <BackButton onClick={() => navigate(-1)}>
            Go Back
          </BackButton>
        </ErrorContainer>
      </ProfileContainer>
    );
  }
  
  // Calculate stats
  const moviesCount = userWatchlist.filter(item => item.type === 'movie').length;
  const showsCount = userWatchlist.filter(item => item.type === 'show').length;
  
  return (
    <ProfileContainer>
      <ProfileHeader>
        <AvatarContainer>
          {user && user.avatar ? (
            <img src={`/avatars/${user.avatar}`} alt="User avatar" />
          ) : (
            <FontAwesomeIcon icon={faUser} />
          )}
        </AvatarContainer>
        
        <UserInfo>
          <Username>@{user && user.username}</Username>
          {user && user.name && <FullName>{user.name}</FullName>}
          {user && user.bio && <Bio>{user.bio}</Bio>}
          
          <JoinDate>
            Member since {user && user.createdAt ? formatDate(user.createdAt) : 'Unknown'}
          </JoinDate>
          
          <UserStats>
            <StatItem>
              <FontAwesomeIcon icon={faFilm} />
              <span>Movies:</span>
              <strong>{moviesCount}</strong>
            </StatItem>
            <StatItem>
              <FontAwesomeIcon icon={faTv} />
              <span>TV Shows:</span>
              <strong>{showsCount}</strong>
            </StatItem>
          </UserStats>
        </UserInfo>
      </ProfileHeader>
      
      <SectionTitle>Watchlist</SectionTitle>
      
      {watchlistError && (
        <ErrorContainer>
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <h3>Error Loading Watchlist</h3>
          <p>{watchlistError}</p>
          <BackButton onClick={clearWatchlistError}>
            Dismiss
          </BackButton>
        </ErrorContainer>
      )}
      
      {loadingWatchlist ? (
        <div className="loading-spinner" />
      ) : userWatchlist.length > 0 ? (
        <WatchlistGrid>
          {userWatchlist.map((item) => (
            <ContentCard 
              key={`${item.type}-${item.id}`}
              item={{
                _id: item.id,
                title: item.title,
                image: item.image,
                releaseYear: item.year,
                type: item.type
              }}
              type={item.type}
            />
          ))}
        </WatchlistGrid>
      ) : (
        <EmptyState>
          <h3>This user's watchlist is empty</h3>
          <p>This user hasn't added any movies or shows to their watchlist yet.</p>
        </EmptyState>
      )}
      
      {user && user._id && (
        <UserComments userId={user._id} isSelf={false} />
      )}
      
    </ProfileContainer>
  );
};

export default UserProfilePage;